<script setup lang="ts">
import { faBook, faMagnifyingGlass, faQuestion } from '@fortawesome/pro-light-svg-icons';
import type { BlogListing } from '@teemill/blog';
import { FontAwesomeIcon, TmlDropdownContainer, TmlForm, TmlInput } from '@teemill/components';
import type { Faq } from '@teemill/faqs';
import type { Project } from '@teemill/projects';
import { watchDebounced } from '@vueuse/core';
import { uniqBy } from 'lodash';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const props = withDefaults(
  defineProps<{
    faqs?: Faq[];
    blogs?: BlogListing[];
    project?: Project;
  }>(),
  {
    faqs: () => [],
    blogs: () => [],
    project: undefined,
  },
);

const router = useRouter();
const query = ref<string>('');
const open = computed({
  get: () => query.value.length > 0,
  set: (value) => {
    if (!value) {
      query.value = '';
    }
  },
});

const emit = defineEmits<{
  (event: 'blur'): void;
  (event: 'focus'): void;
  (event: 'update:searchResultsLength', length: number): void;
}>();

const routes = computed(() => {
  return uniqBy(
    router
      .getRoutes()
      .filter((route) => route.meta.search?.show !== false)
      .filter((route) => route.meta.title !== undefined)
      .filter((route) => !route.path.includes(':'))
      .filter((route) => {
        const searchMeta = route.meta.search as any;
        if (searchMeta?.excludeAlias && route.aliasOf) {
          return false;
        }
        return true;
      })
      .filter((route) => {
        const nameMatches = String(route.name).toLowerCase().includes(query.value.toLowerCase());

        const searchTitleMatches =
          route.meta.search?.title?.toLowerCase().includes(query.value.toLowerCase()) || false;

        const pageTitleMatches =
          route.meta?.title &&
          typeof route.meta.title === 'string' &&
          route.meta.title.toLowerCase().includes(query.value.toLowerCase());

        return nameMatches || searchTitleMatches || pageTitleMatches;
      }),
    'path',
  );
});

const searchResults = computed(() => {
  const routeResults = routes.value;

  const blogResults = processBlogs(filteredBlogs.value);

  const faqResults = processFaqs(filteredFaqs.value);

  return [...routeResults, ...blogResults, ...faqResults];
});

const filteredBlogs = computed(() => {
  if (!props.blogs) return [];
  return props.blogs.filter((blog) => blog.title.toLowerCase().includes(query.value.toLowerCase()));
});

const filteredFaqs = computed(() => {
  if (!props.faqs) return [];
  return props.faqs.filter((faq) => faq.question.toLowerCase().includes(query.value.toLowerCase()));
});

const processBlogs = (blogs: BlogListing[]) => {
  const path = '/support/tutorials/';
  return blogs.map((blog) => ({
    name: blog.title,
    path: path + blog.slug,
    icon: faBook,
    parent: null,
    meta: {
      title: blog.title,
    },
  }));
};

const processFaqs = (faqs: Faq[]) => {
  return faqs.map((faq) => ({
    name: faq.question,
    path: `/support/faqs/${faq.id}`,
    icon: faQuestion,
    parent: null,
    meta: {
      title: faq.question,
    },
  }));
};

const searchQuery = defineModel<string>('query', { default: '' });

watchDebounced(
  query,
  (val) => {
    searchQuery.value = val;
    emit('update:searchResultsLength', searchResults.value.length);
  },
  {
    debounce: 500,
  },
);
</script>

<template>
  <div class="relative">
    <TmlForm autocomplete="off">
      <label for="search" class="sr-only">Search</label>
      <TmlInput
        v-model="query"
        name="search"
        type="search"
        :class="['rounded-full bg-[--tml-bubble-background-color]']"
        @blur="emit('blur')"
        @focus="emit('focus')"
      >
        <template #before>
          <FontAwesomeIcon class="text-gray-500 ml-4" :icon="faMagnifyingGlass" />
        </template>
      </TmlInput>
    </TmlForm>

    <TmlDropdownContainer v-model:open="open" width="100%" as="dropdown">
      <div class="flex flex-col w-full">
        <template v-if="searchResults.length === 0">
          <span
            class="justify-center flex flex-col p-4 py-2 first:pt-2 last:pb-2 !text-[--tml-menu-text-color] hover:bg-[--tml-menu-hover-background-color] min-h-16 text-lg"
          >
            No Results.
          </span>
        </template>
        <template v-else>
          <RouterLink
            v-for="result in searchResults"
            :key="result.path"
            :to="result.path + (project ? `?project=${project.id}` : '')"
            class="justify-center flex flex-col p-4 py-2 first:pt-2 last:pb-2 !text-[--tml-menu-text-color] hover:bg-[--tml-menu-hover-background-color] min-h-16 border-b border-[--tml-menu-border-color] last:border-b-0"
            @click="open = false"
          >
            <div class="flex gap-2">
              <FontAwesomeIcon v-if="'icon' in result" :icon="result.icon" class="mt-1 mr-2" />
              <span class="text-lg">{{
                (result.meta as any).search?.title ??
                (typeof result.meta.title === 'function'
                  ? result.meta.title({} as any)
                  : result.meta.title)
              }}</span>
            </div>
          </RouterLink>
        </template>
      </div>
    </TmlDropdownContainer>
  </div>
</template>
